import Input, {Size} from '@jetbrains/ring-ui/components/input/input'
import classNames from 'classnames'
import type {ChangeEvent, FC} from 'react'
import React, {memo, useCallback, useContext, useId, useRef} from 'react'

import Markdown, {LinkOpenPolicy} from '../../../../../../../../../../common/Markdown/Markdown'
import {DisabledContext} from '../../../../../../../../contexts/DisabledContext'
import {useStoredSettingsValue} from '../../../../../../../../contexts/StoredSettingsValue/StoredSettingsValueContext'
import useBooleanState from '../../../../../../../../hooks/useBooleanState'
import {useInlineInput} from '../../../../../../../../hooks/useInlineInput'
import Details from '../../../../../../Details/Details'
import {EditButton} from '../../../../../../EditButton/EditButton'
import type {
  SmartDetectorProps,
  SmartDetectorSuccessProps,
} from '../SmartDetectors/SmartDetectors.types'
import TogglableProperty from '../TogglableProperty/TogglableProperty'

import styles from './JobStepStringProperty.css'

type Props = {
  placeholder?: string | null
  name: string
  label?: string
  type: string
  value: string | null
  stepIndex: number
  onChange: (stepIndex: number, name: string, value: string | null) => void
  multiline?: boolean
  monospace?: boolean
  smartDetectors?: FC<SmartDetectorProps>[]
  smartDetectorsSuccess?: FC<SmartDetectorSuccessProps>[]
  autoFocus?: boolean
  updated?: boolean
  inline?: boolean
  description?: string | null
  toggleable?: boolean
  withAdvanced?: boolean
}

function JobStepStringProperty({
  placeholder,
  multiline,
  monospace,
  smartDetectors,
  smartDetectorsSuccess,
  autoFocus,
  stepIndex,
  onChange,
  value,
  name,
  label,
  inline,
  description,
  toggleable,
  type,
  withAdvanced,
}: Props) {
  const {setValueToStoredSettings, getValueFromStoredSettings} = useStoredSettingsValue()
  const storedValueKey = `${type}_${name}_${stepIndex}`
  const storedValue = (getValueFromStoredSettings(storedValueKey) ?? '') as string
  const [focused, onFocus, onBlur] = useBooleanState()
  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(stepIndex, name, event.currentTarget.value)
    },
    [stepIndex, name, onChange],
  )

  const enableSmartDetector = smartDetectors != null
  const isDisabled = useContext(DisabledContext)
  const [isEditing, setIsEditing, inputHandlers] = useInlineInput()

  const inputRef = useRef<HTMLInputElement>(null)
  const id = useId()
  const isToggledOff = toggleable && value == null

  const details =
    description != null && description !== '' ? (
      <Details>
        <Markdown className={styles.description} linkOpenPolicy={LinkOpenPolicy.ALL_IN_NEW_TAB}>
          {description}
        </Markdown>
      </Details>
    ) : null
  const input = (
    <Input
      multiline={multiline}
      inputRef={inputRef as never}
      size={Size.FULL}
      inputClassName={classNames(styles.input, {
        [styles.multiline]: multiline,
        [styles.monospace]: monospace,
        [styles.toggleable]: toggleable,
      })}
      placeholder={placeholder ?? undefined}
      value={value != null ? value : ''}
      onChange={onInputChange}
      spellCheck={false}
      onFocus={enableSmartDetector ? onFocus : undefined}
      {...(inline ? inputHandlers : {})}
      onBlur={() => {
        if (enableSmartDetector) {
          onBlur()
        }
        if (inline) {
          inputHandlers.onBlur()
        }
      }}
      autoFocus={autoFocus || inline}
      disabled={isDisabled}
      label={
        inline || toggleable ? undefined : (
          <>
            {label}
            {details}
          </>
        )
      }
      aria-label={label}
      aria-labelledby={toggleable ? id : undefined}
    />
  )

  const onToggleChange = useCallback(
    ({currentTarget}: ChangeEvent<HTMLInputElement>) => {
      const {checked} = currentTarget

      if (!checked) {
        setValueToStoredSettings(storedValueKey, value)
      }

      onChange(stepIndex, name, checked ? storedValue : null)

      if (checked) {
        requestAnimationFrame(() => inputRef.current?.focus())
      }
    },
    [name, onChange, setValueToStoredSettings, stepIndex, storedValue, storedValueKey, value],
  )

  return (
    <div className={styles.container}>
      {toggleable ? (
        <TogglableProperty
          label={label}
          details={details}
          onChange={onToggleChange}
          checked={value != null}
        >
          {input}
        </TogglableProperty>
      ) : inline ? (
        <label className={styles.inline}>
          {`${label}: `}
          {isEditing ? (
            input
          ) : (
            <>
              {value || placeholder}
              {details}
              <EditButton onClick={() => setIsEditing(true)} />
            </>
          )}
        </label>
      ) : (
        input
      )}
      {smartDetectors?.map((SmartDetector, i) => (
        <SmartDetector
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          anchorElement={inputRef.current}
          name={name}
          disabled={isToggledOff}
          value={value ?? ''}
          stepIndex={stepIndex}
          focused={focused}
          withAdvanced={withAdvanced}
        />
      ))}
      {smartDetectorsSuccess?.map((SmartDetectorSuccess, i) => (
        <SmartDetectorSuccess
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          stepIndex={stepIndex}
        />
      ))}
    </div>
  )
}

export default memo<Props>(JobStepStringProperty)
